import http from 'hub-http/clients/apiClient';
import { Fragment } from 'react';
import { getUnifiedAuthConfigUrl } from './Api';
import { chirpClient } from './SyncBridge';
import { isEpicApp } from './constants/appIds';
import { AuthenticateError, DuplicateDataSyncAccountError, UnifiedAuthView } from './LowLevelUnifiedAuth';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { UniversalAuthBeamTracker } from './UsageTracker';
import { hubletApi, resolveApi } from 'hub-http/middlewares/core';
import { buildUrl } from 'hub-http/helpers/url';
import { stringify } from 'hub-http/helpers/params';
import { userInfoSync } from 'hub-http/userInfo';
import { gate } from 'hub-http/gates';
import enviro from 'enviro';
import { View } from 'foundations-components/transitional/components/View';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Button } from 'foundations-components/transitional/components/Button';
import { StepIndicator } from 'foundations-components/transitional/components/StepIndicator';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogHeaderImage } from 'foundations-components/transitional/components/DialogHeaderImage';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { getAppRpc } from '../__generated__/chirp/com/hubspot/piesync/bridge/rpc/AppsRpc';
import { authenticateNewAccountRpc, reauthenticateAccountRpc } from '../__generated__/chirp/com/hubspot/piesync/bridge/rpc/AccountsRpc';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function fetchAuthConfigForDataSync(portalId, appId, accountId, hubletOverride) {
  return async () => {
    const numericAppId = Number(appId);
    const {
      app: appResponse
    } = await chirpClient.call(getAppRpc, {
      request: {
        appId: numericAppId
      }
    });
    const app = appResponse.applicationListing;
    const {
      authenticatorId,
      scope
    } = appResponse.authInfo;
    const authFlow = await http.get(buildUrl({
      hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
      path: `/api/${getUnifiedAuthConfigUrl(authenticatorId)}`,
      query: stringify({
        appName: app.name
      })
    }));
    return {
      appName: appResponse.displayName || app.name,
      appIcon: app.iconUrl || app.extensionIconUrl || '',
      oAuthScope: scope,
      authFlow,
      getAuthorizeUrl: query => http.get(buildUrl({
        hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
        path: `/api/unified-auth/v1/authenticators/${authenticatorId}/authorize-url`
      }), {
        query
      }).then(response => {
        if (typeof response === 'object' && 'status' in response && response.status === 'error') {
          throw new AuthenticateError(response.message, response.upstreamErrorMessage);
        }
        return response;
      }),
      finishAuthFlow(payload) {
        const toAccount = remoteAccount => ({
          id: remoteAccount.id,
          accountName: remoteAccount.accountName,
          accountIdentifier: remoteAccount.accountIdentifier,
          connectionIds: remoteAccount.connectionIds.map(String)
        });

        // step 1: obtain `secretId` from Unified Auth
        return http.post(buildUrl({
          hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
          path: `/api/unified-auth/v1/authenticators/${authenticatorId}/secret`
        }), {
          data: payload
        }).then(finishPayload => {
          if (typeof finishPayload === 'object' && 'status' in finishPayload && finishPayload.status === 'error') {
            throw new AuthenticateError(finishPayload.message, finishPayload.upstreamErrorMessage);
          } else {
            return finishPayload;
          }
        })
        // step 2: use `secretId` to create an account through the Sync Bridge via CHIRP
        .then(({
          secretId
        }) => {
          if (accountId !== undefined) {
            return chirpClient.callWithResultUnion(reauthenticateAccountRpc, {
              request: {
                accountId: Number(accountId),
                secretId
              }
            }).then(result => {
              if (result.type === 'rpcError') {
                throw new AuthenticateError(result.rpcError.chirpErrorMessage);
              }
              return toAccount(result.data);
            });
          }
          return chirpClient.callWithResultUnion(authenticateNewAccountRpc, {
            request: {
              appId: numericAppId,
              secretId
            }
          }).then(result => {
            if (result.type === 'rpcError') {
              const error = result.rpcError;
              if (error.reason === 'SYNC_ALREADY_INSTALLED' && error.duplicateAccountId) {
                var _error$accountName, _error$accountIdentif;
                throw new DuplicateDataSyncAccountError(portalId, numericAppId, {
                  id: Number(error.duplicateAccountId),
                  accountName: (_error$accountName = error.accountName) !== null && _error$accountName !== void 0 ? _error$accountName : '',
                  accountIdentifier: (_error$accountIdentif = error.accountIdentifier) !== null && _error$accountIdentif !== void 0 ? _error$accountIdentif : '',
                  connectionIds: []
                });
              }
              throw new AuthenticateError(error.chirpErrorMessage);
            }
            return toAccount(result.data);
          });
        });
      }
    };
  };
}
export function DataSyncIntroScreen({
  appName
}) {
  return /*#__PURE__*/_jsx(Flex, {
    "data-test-id": "data-sync-intro",
    children: /*#__PURE__*/_jsx(StepIndicator, {
      direction: "vertical",
      stepIndex: 0,
      stepNames: [/*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "p",
          typography: "heading-50",
          textTransform: "none",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.connect",
            options: {
              appName
            }
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.auth.connectDescription",
            options: {
              appName
            }
          })
        })]
      }, 0), /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "p",
          typography: "heading-50",
          textTransform: "none",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.dataSync.step.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.dataSync.step.description"
          })
        })]
      }, 1)]
    })
  });
}
const WAREHOUSE_APPS = {
  BIGQUERY: {
    QA: 14497998,
    PROD: 6854315,
    directionGates: [gate('Warehouse::DirectIngestionAndDataOut::Release::BigQuery')]
  },
  SNOWFLAKE: {
    QA: 7270771,
    PROD: 2963325,
    directionGates: [gate('Warehouse::DirectIngestion::Release::Snowflake'), gate('Warehouse::DataOut::Release::Snowflake')]
  },
  S3: {
    QA: 21134820,
    PROD: 15928931,
    directionGates: [gate('Warehouse::DirectIngestion::Release::S3'), gate('Warehouse::DataOut::Release::S3')]
  },
  DATABRICKS: {
    QA: 23516730,
    PROD: 19173091,
    directionGates: [gate('Warehouse::DirectIngestionAndDataOut::Release::Databricks')]
  },
  REDSHIFT: {
    QA: 25868157,
    PROD: 23504435,
    directionGates: [gate('Warehouse::DirectIngestionAndDataOut::Release::Redshift')]
  },
  SYNAPSE: {
    QA: 25868339,
    PROD: 23852722,
    directionGates: [gate('Warehouse::DirectIngestionAndDataOut::Release::Synapse')]
  },
  JDBC_POSTGRES: {
    QA: 39376698,
    PROD: 42328502,
    directionGates: [gate('DatasetsManager:WarehouseDataSources:Jdbc')]
  },
  JDBC_MYSQL: {
    QA: 39376754,
    PROD: 42328628,
    directionGates: [gate('DatasetsManager:WarehouseDataSources:Jdbc')]
  }
};
function isUngatedForDataIntegrationHome(appId) {
  try {
    const {
      gates
    } = userInfoSync();
    const env = enviro.isQa() ? 'QA' : 'PROD';
    const appIdStr = String(appId);
    for (const warehouseApp of Object.values(WAREHOUSE_APPS)) {
      if (String(warehouseApp[env]) === appIdStr) {
        return warehouseApp.directionGates.some(g => gates.includes(g));
      }
    }
    return false;
  } catch (_unused) {
    return false;
  }
}
function isSuperAdmin() {
  try {
    return userInfoSync().user.scopes.includes('super-admin');
  } catch (_unused2) {
    return false;
  }
}
function useContinueSetupHref(portalId, appId, accountId) {
  if (isUngatedForDataIntegrationHome(appId)) {
    return `/data-integration-home/${portalId}?appId=${appId}&accountId=${accountId}&tab=apps`;
  }
  if (isEpicApp(appId) && isSuperAdmin()) {
    return `/data-sync/${portalId}/app/${appId}/account/${accountId}/epic-settings`;
  }
  return `/data-sync/${portalId}/app/${appId}/account/${accountId}/settings/object-view`;
}
export function DataSyncSuccessScreen({
  portalId,
  appId,
  appName,
  account,
  onOpenExternalLink,
  onSetupSyncClick
}) {
  return /*#__PURE__*/_jsxs(DialogBody, {
    children: [/*#__PURE__*/_jsx(Flex, {
      children: /*#__PURE__*/_jsx(StepIndicator, {
        direction: "vertical",
        stepIndex: 1,
        stepNames: [/*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "p",
            typography: "heading-50",
            textTransform: "none",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.connect",
              options: {
                appName
              }
            })
          }), /*#__PURE__*/_jsx(MediaObject, {
            "data-test-id": "data-sync-account-avatar",
            paddingInline: "600",
            itemLeft: /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              justifyContent: "center",
              children: /*#__PURE__*/_jsx(IconCircle, {
                icon: ContactsIcon,
                color: "border-core-default",
                backgroundColor: "fill-primary-disabled",
                borderWidth: "0",
                size: "100"
              })
            }),
            children: /*#__PURE__*/_jsx(Flex, {
              justifyContent: "space-between",
              children: /*#__PURE__*/_jsxs(View, {
                children: [/*#__PURE__*/_jsx(Text, {
                  textTransform: "none",
                  children: account.accountName
                }), account.accountIdentifier !== '' && /*#__PURE__*/_jsx(View, {
                  children: /*#__PURE__*/_jsx(Text, {
                    as: "small",
                    use: "help",
                    children: account.accountIdentifier
                  })
                })]
              })
            })
          })]
        }, 0), /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "p",
            typography: "heading-50",
            textTransform: "none",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.dataSync.step.title"
            })
          }), /*#__PURE__*/_jsx(Text, {
            as: "small",
            use: "help",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.dataSync.step.description"
            })
          })]
        }, 1)]
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingBlock: "600",
      as: "footer",
      children: /*#__PURE__*/_jsx(ButtonWrapper, {
        justify: "start",
        children: /*#__PURE__*/_jsx(Button, {
          "data-test-id": "continue-setup-button",
          use: "primary",
          href: useContinueSetupHref(portalId, appId, account.id),
          target: "_top",
          onClick: event => {
            UniversalAuthBeamTracker.track('sync-setup', {
              applicationId: appId,
              applicationName: appName,
              action: 'START_SYNC_SETUP'
            });
            if (onSetupSyncClick) {
              onSetupSyncClick(event);
            }
            if (onOpenExternalLink) {
              onOpenExternalLink(event);
            }
          },
          responsive: false,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.dataSync.continueSetup"
          })
        })
      })
    })]
  });
}
export function DataSyncReauthForm({
  portalId,
  appId,
  appName,
  account,
  onComplete
}) {
  return /*#__PURE__*/_jsx(UnifiedAuthView, {
    isModal: false,
    isReauth: true,
    portalId: portalId,
    fetchAuthConfig: fetchAuthConfigForDataSync(portalId, appId, account.id, undefined /* don't override the hublet */),
    onComplete: onComplete,
    onCancel: () => {},
    introScreen: ({
      type
    }) => type === 'OAuth' ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AccountTile, {
        account: account
      }), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "universalAuth.dataSync.reauthenticate.oAuthNote_jsx",
            elements: {
              strong: 'strong'
            },
            options: {
              appName
            }
          })
        })
      })]
    }) : null
  });
}
export function AccountTile({
  account
}) {
  return /*#__PURE__*/_jsx(Tile, {
    compact: true,
    marginBlock: "400",
    children: /*#__PURE__*/_jsx(TileSection, {
      children: /*#__PURE__*/_jsx(MediaObject, {
        itemLeft: /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(IconCircle, {
            icon: ContactsIcon,
            color: "border-core-default",
            backgroundColor: "fill-primary-disabled",
            borderWidth: "0",
            size: "400"
          })
        }),
        children: /*#__PURE__*/_jsx(Flex, {
          justifyContent: "space-between",
          children: /*#__PURE__*/_jsxs(View, {
            children: [/*#__PURE__*/_jsx(Text, {
              textTransform: "none",
              children: account.accountName
            }), account.accountIdentifier !== '' && /*#__PURE__*/_jsx(View, {
              children: /*#__PURE__*/_jsx(Text, {
                as: "small",
                use: "help",
                children: account.accountIdentifier
              })
            })]
          })
        })
      })
    })
  });
}
export function DataSyncReconnectModal({
  portalId,
  appId,
  account,
  onComplete,
  onCancel
}) {
  return /*#__PURE__*/_jsx(UnifiedAuthView, {
    isModal: true,
    isReauth: true,
    portalId: portalId,
    fetchAuthConfig: fetchAuthConfigForDataSync(portalId, appId, account.id, undefined /* don't override the hublet */),
    onComplete: onComplete,
    onCancel: onCancel,
    introScreen: ({
      appName
    }) => /*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "p",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "universalAuth.dataSync.reconnectModal.description_jsx",
          options: {
            appName
          },
          elements: {
            strong: 'strong'
          }
        })
      }), /*#__PURE__*/_jsx(AccountTile, {
        account: account
      })]
    })
  });
}
export function DataSyncSuccessDialog(props) {
  const {
    appName,
    onCancel,
    portalId,
    appId,
    authResponse,
    onSetupSyncClick,
    onDoThisLaterClick,
    onOpenExternalLink
  } = props;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DialogHeaderImage, {
      offsetBottom: -30,
      offsetTop: 59,
      children: /*#__PURE__*/_jsx(Illustration, {
        alt: "Illustration of a green checkmark",
        name: "success-green",
        width: 118
      })
    }), /*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.modal.successTitle",
          options: {
            appName
          }
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      })]
    }), /*#__PURE__*/_jsx(DataSyncSuccessScreen, {
      portalId: portalId,
      appId: appId,
      appName: appName,
      account: authResponse,
      onOpenExternalLink: onOpenExternalLink,
      onSetupSyncClick: onSetupSyncClick,
      onDoThisLaterClick: onDoThisLaterClick
    })]
  });
}